/**
 * Web Chat API client.
 *
 * Wraps the two backend endpoints from the cdk PR-2 web-chat bridge:
 *
 *   POST /chat/messages       send a chat message → signalWithStarts
 *                              ariConversationWorkflow with channel='WEB'
 *   POST /chat/messages/list  poll outbound messages on a chatSessionId
 *
 * The ChatPanelContext consumes these via the useChatSocket hook (which
 * really polls today; WebSocket push lands in a follow-up commit alongside
 * the API Gateway WebSocket Lambda in cdk).
 */

import { createApiRequest } from "src/api/common/apiClient";

// ─── Request / response types ─────────────────────────────────────────

export interface SendChatMessageRequest {
  /** Frontend-generated UUID, stable per ChatPanelContext session. */
  chatSessionId: string;
  bodyText: string;
  /** Optional contextual references (deals/documents pinned into the prompt). */
  contextItems?: Array<{ kind: string; id: string; label?: string }>;
  /** 'global' | 'deal:<id>' | 'document:<id>' */
  scope?: string;
  /** P2c: file IDs already in the user's vault to attach to this turn.
   *  Each fileId must be readable by the sender (org membership). */
  attachmentFileIds?: string[];
}

export interface SendChatMessageResponse {
  providerMessageId: string;
  workflowId: string;
  acceptedAt: string;
}

export interface ListChatMessagesRequest {
  chatSessionId: string;
  /** Return messages with createdAt strictly after this ISO timestamp. */
  afterIso?: string;
  /** Cap the page size. Backend default is 50; 200 is the cap. */
  limit?: number;
}

export interface WebChatMessage {
  webChatMessageId: string;
  chatSessionId: string;
  /** 'inbound' (user) | 'outbound' (Ari) */
  direction: "inbound" | "outbound";
  /** 'message' | 'typing' | 'tool_start' | 'tool_done' | 'deal_progress' */
  kind: "message" | "typing" | "tool_start" | "tool_done" | "deal_progress";
  bodyText: string | null;
  toolEvents: any;
  createdAt: string;
}

export interface ListChatMessagesResponse {
  messages: WebChatMessage[];
  /** Pass back as `afterIso` on the next call to continue the cursor. */
  nextAfterIso: string | null;
}

export interface ListChatSessionsRequest {
  limit?: number;
}

export interface ChatSessionSummary {
  chatSessionId: string;
  title: string;
  firstAt: string;
  lastAt: string;
  messageCount: number;
}

export interface ListChatSessionsResponse {
  sessions: ChatSessionSummary[];
}

// ─── API request functions ────────────────────────────────────────────

export const sendChatMessageApi = createApiRequest<SendChatMessageResponse>(
  "post",
  "/chat/messages",
);

export const listChatMessagesApi = createApiRequest<ListChatMessagesResponse>(
  "post",
  "/chat/messages/list",
);

export const listChatSessionsApi = createApiRequest<ListChatSessionsResponse>(
  "post",
  "/chat/sessions/list",
);
